import { useCallback, useEffect, useRef, useState } from 'react';
import {
  runAgentic,
  sendAgenticMessage,
  fetchAgenticSessions,
  fetchAgenticSessionById,
} from '../services/agenticService';

const DEFAULT_SESSIONS_PAGINATION = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 1,
};

const DEFAULT_TIMELINE_PAGINATION = {
  page: 1,
  limit: 200,
  total: 0,
  totalPages: 1,
};

const getFriendlyErrorMessage = (error, fallbackMessage) => {
  const statusCode = error?.response?.status;

  if (statusCode === 400) return 'Invalid request. Please check your prompt and try again.';
  if (statusCode === 401) return 'Your session expired. Please login again.';
  if (statusCode === 404) return 'Session not found. Refresh and try again.';
  if (statusCode === 500) return 'Server error occurred. Please retry in a moment.';

  return error?.response?.data?.message || error?.message || fallbackMessage;
};

export const useAgentic = () => {
  const mountedRef = useRef(true);

  const [sessions, setSessions] = useState([]);
  const [sessionsPagination, setSessionsPagination] = useState(DEFAULT_SESSIONS_PAGINATION);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [selectedSession, setSelectedSession] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [timelinePagination, setTimelinePagination] = useState(DEFAULT_TIMELINE_PAGINATION);

  const [latestRun, setLatestRun] = useState(null);
  const [lastMessageInput, setLastMessageInput] = useState('');

  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);

  const [error, setError] = useState(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadSessions = useCallback(async (page = 1, limit = 20) => {
    try {
      setSessionsLoading(true);
      setError(null);

      const response = await fetchAgenticSessions(page, limit);
      const items = response?.data?.items || [];
      const pagination = response?.data?.pagination || {
        page,
        limit,
        total: items.length,
        totalPages: 1,
      };

      if (!mountedRef.current) return response;

      setSessions(items);
      setSessionsPagination(pagination);

      if (!selectedSessionId && items.length > 0) {
        setSelectedSessionId(items[0]._id);
      }

      return response;
    } catch (fetchError) {
      if (!mountedRef.current) throw fetchError;

      const message = getFriendlyErrorMessage(fetchError, 'Failed to fetch sessions');
      setError({ message, statusCode: fetchError?.response?.status });
      throw fetchError;
    } finally {
      if (mountedRef.current) {
        setSessionsLoading(false);
      }
    }
  }, [selectedSessionId]);

  const loadSessionDetails = useCallback(async (sessionId, page = 1, limit = 200) => {
    if (!sessionId) return null;

    try {
      setDetailsLoading(true);
      setError(null);

      const response = await fetchAgenticSessionById(sessionId, page, limit);
      const session = response?.data?.session || null;
      const timeline = response?.data?.interactions || [];
      const pagination = response?.data?.pagination || {
        page,
        limit,
        total: timeline.length,
        totalPages: 1,
      };

      if (!mountedRef.current) return response;

      setSelectedSession(session);
      setInteractions(timeline);
      setTimelinePagination(pagination);

      return response;
    } catch (fetchError) {
      if (!mountedRef.current) throw fetchError;

      const message = getFriendlyErrorMessage(fetchError, 'Failed to fetch session details');
      setError({ message, statusCode: fetchError?.response?.status });
      throw fetchError;
    } finally {
      if (mountedRef.current) {
        setDetailsLoading(false);
      }
    }
  }, []);

  const run = useCallback(async ({ input, sessionKey }) => {
    try {
      setRunLoading(true);
      setError(null);

      const response = await runAgentic(input, sessionKey);
      const runData = response?.data || null;

      if (!mountedRef.current) return response;

      setLatestRun(runData);

      if (runData?.sessionId) {
        setSelectedSessionId(runData.sessionId);
        await loadSessionDetails(runData.sessionId, 1, timelinePagination.limit || 200);
      }

      await loadSessions(sessionsPagination.page || 1, sessionsPagination.limit || 20);

      return response;
    } catch (runError) {
      if (!mountedRef.current) throw runError;

      const message = getFriendlyErrorMessage(runError, 'Failed to run agentic workflow');
      setError({ message, statusCode: runError?.response?.status });
      throw runError;
    } finally {
      if (mountedRef.current) {
        setRunLoading(false);
      }
    }
  }, [loadSessionDetails, loadSessions, sessionsPagination.limit, sessionsPagination.page, timelinePagination.limit]);

  const sendMessage = useCallback(async ({ input, sessionKey }) => {
    const trimmedInput = (input || '').trim();
    if (!trimmedInput) {
      throw new Error('Message is required');
    }

    try {
      setRunLoading(true);
      setError(null);
      setLastMessageInput(trimmedInput);

      let response;
      let activeSessionId = selectedSessionId;

      if (activeSessionId) {
        response = await sendAgenticMessage(activeSessionId, trimmedInput);
      } else {
        response = await runAgentic(trimmedInput, sessionKey);
        activeSessionId = response?.data?.sessionId || null;
      }

      const turnData = response?.data || null;

      if (!mountedRef.current) return response;

      setLatestRun(turnData);

      const nextSessionId = turnData?.sessionId || activeSessionId;
      if (nextSessionId) {
        setSelectedSessionId(nextSessionId);
        await loadSessionDetails(nextSessionId, 1, timelinePagination.limit || 200);
      }

      await loadSessions(sessionsPagination.page || 1, sessionsPagination.limit || 20);
      return response;
    } catch (messageError) {
      if (!mountedRef.current) throw messageError;

      const message = getFriendlyErrorMessage(messageError, 'Failed to send message');
      setError({ message, statusCode: messageError?.response?.status });

      if (selectedSessionId) {
        try {
          await loadSessionDetails(selectedSessionId, 1, timelinePagination.limit || 200);
        } catch (refreshError) {
          // no-op: preserve original message error
        }
      }

      throw messageError;
    } finally {
      if (mountedRef.current) {
        setRunLoading(false);
      }
    }
  }, [loadSessionDetails, loadSessions, selectedSessionId, sessionsPagination.limit, sessionsPagination.page, timelinePagination.limit]);

  const retryLastMessage = useCallback(async () => {
    if (!lastMessageInput) {
      throw new Error('No previous message to retry');
    }

    return sendMessage({ input: lastMessageInput });
  }, [lastMessageInput, sendMessage]);

  useEffect(() => {
    loadSessions().catch(() => {});
  }, [loadSessions]);

  useEffect(() => {
    if (!selectedSessionId) return;
    loadSessionDetails(selectedSessionId, 1, timelinePagination.limit || 200).catch(() => {});
  }, [loadSessionDetails, selectedSessionId, timelinePagination.limit]);

  return {
    sessions,
    sessionsPagination,
    selectedSessionId,
    selectedSession,
    interactions,
    timelinePagination,
    latestRun,
    sessionsLoading,
    detailsLoading,
    runLoading,
    error,
    lastMessageInput,
    setSelectedSessionId,
    loadSessions,
    loadSessionDetails,
    run,
    sendMessage,
    retryLastMessage,
  };
};

export default useAgentic;